import React, { useState, type JSX } from 'react';
import arrowDown from '@/assets/svg/static/arrowDown.svg';
import igIcon from '@/assets/svg/static/ig.svg';
import mail2Icon from '@/assets/svg/static/mail2.svg';
import slaydTextIcon from '@/assets/svg/static/slaydText.svg';
import whatsappIcon from '@/assets/svg/static/whatsapp.svg';
import {
  EVENTS,
  PROPERTIES,
  trackEvent,
  TRACKING_SOURCES,
} from '@/services/posthog';
import { capitalizeFirstLetter } from '@/utils/formatting';
import {
  DEFAULT_PRIVACY_POLICY,
  DEFAULT_RETURN_POLICY,
  DEFAULT_SHIPPING_POLICY,
} from '../ProductDetailPageV2.constants';
import styles from './ProductFooter.module.css';
import type { ProductFooterProps } from './ProductFooter.types';

export const ProductFooter = React.memo(
  ({ curatorName, productDetails }: ProductFooterProps): JSX.Element => {
    const influencerSlug = curatorName
      ? capitalizeFirstLetter(curatorName)
      : null;

    const [activeAccordion, setActiveAccordion] = useState<{
      return: boolean;
      shipping: boolean;
      privacy: boolean;
    }>({
      return: false,
      shipping: false,
      privacy: false,
    });

    const toggleAccordion = (
      section: 'return' | 'shipping' | 'privacy'
    ): void => {
      const nextVal = !activeAccordion[section];
      trackEvent(EVENTS.PDP_V2_POLICY_ACCORDION_CLICK, {
        [PROPERTIES.PRODUCT_ID]: productDetails?.id,
        [PROPERTIES.SECTION]: section,
        [PROPERTIES.IS_OPEN]: nextVal,
        [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
      });
      setActiveAccordion((prev) => ({
        ...prev,
        [section]: nextVal,
      }));
    };

    const handleSocialClick = (
      platform: 'instagram' | 'email' | 'whatsapp'
    ): void => {
      trackEvent(EVENTS.PDP_V2_FOOTER_SOCIAL_CLICK, {
        [PROPERTIES.PRODUCT_ID]: productDetails?.id,
        [PROPERTIES.PLATFORM]: platform,
        [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
      });
    };

    return (
      <div className={styles.creatorFooter}>
        {influencerSlug ? (
          <>
            <h2 className={styles.footerBranding}>
              <img
                src={slaydTextIcon}
                alt="Slayd"
                className={styles.slaydTextLogo}
              />
              <span className={styles.xText}>x</span>
              <span className={styles.originalsTitleCursive}>
                {influencerSlug}
              </span>
            </h2>
            <p className={styles.footerBio}>
              Together with talented fashion creators like {influencerSlug},
              Slayd brings the most-loved trends from inspiration to your
              wardrobe 🫶
            </p>
          </>
        ) : (
          <>
            <h2 className={styles.footerBranding}>
              <img
                src={slaydTextIcon}
                alt="Slayd"
                className={styles.slaydTextLogo}
              />
            </h2>
            <p className={styles.footerBio}>
              Slayd brings the most-loved trends from inspiration to your
              wardrobe 🫶
            </p>
          </>
        )}

        <div className={styles.dividerLine} />

        <div>
          <div
            className={styles.accordionHeader}
            onClick={() => toggleAccordion('return')}
          >
            <span>Return/ Refund Policy</span>
            <img
              src={arrowDown}
              className={`${styles.accordionChevron} ${activeAccordion.return ? styles.chevronRotate : ''}`}
              alt="Chevron"
            />
          </div>
          {activeAccordion.return && (
            <div className={styles.accordionContent}>
              {DEFAULT_RETURN_POLICY}
            </div>
          )}
        </div>

        <div>
          <div
            className={styles.accordionHeader}
            onClick={() => toggleAccordion('shipping')}
          >
            <span>Shipping Policy</span>
            <img
              src={arrowDown}
              className={`${styles.accordionChevron} ${activeAccordion.shipping ? styles.chevronRotate : ''}`}
              alt="Chevron"
            />
          </div>
          {activeAccordion.shipping && (
            <div className={styles.accordionContent}>
              {DEFAULT_SHIPPING_POLICY}
            </div>
          )}
        </div>

        <div>
          <div
            className={styles.accordionHeader}
            onClick={() => toggleAccordion('privacy')}
          >
            <span>Privacy Policy</span>
            <img
              src={arrowDown}
              className={`${styles.accordionChevron} ${activeAccordion.privacy ? styles.chevronRotate : ''}`}
              alt="Chevron"
            />
          </div>
          {activeAccordion.privacy && (
            <div className={styles.accordionContent}>
              {DEFAULT_PRIVACY_POLICY}
            </div>
          )}
          <div className={styles.dividerLine} />
        </div>

        <div className={styles.socialIconsRow}>
          <a
            href="https://www.instagram.com/slayd_finds/"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.socialIconCircle}
            onClick={() => handleSocialClick('instagram')}
          >
            <img src={igIcon} alt="Instagram" />
          </a>
          <a
            href="mailto:hello@slayd.in"
            className={styles.socialIconCircle}
            onClick={() => handleSocialClick('email')}
          >
            <img src={mail2Icon} alt="Email" />
          </a>
          <a
            href={`https://wa.me/916395347884?text=${encodeURIComponent(
              'Hey Slayd! I have a question.'
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.socialIconCircle}
            onClick={() => handleSocialClick('whatsapp')}
          >
            <img src={whatsappIcon} alt="WhatsApp" />
          </a>
        </div>

        <div className={styles.shortDivider} />

        <div className={styles.copyrightText}>Slayd Technologies Pvt Ltd</div>
      </div>
    );
  }
);

export default ProductFooter;
